// Auditoria do Pedido (épico PO1-3237): cotado × conferido × cobrado de um
// pedido, com os CT-es, as faturas, os indícios e as pendências (dossiê do
// backend, H1), o relatório narrativo e o PDF (H3) e a decisão em lote das
// faturas do pedido (H5). Entradas: busca direta, botão "Auditar frete" da
// cotação e link da Auditoria de Faturas, sempre por `?pedido=`.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Col, Input, Row, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import MetricCardGroup from "components/MetricCardGroup";
import MetricCard from "components/MetricCard";
import ShellCard, { TableSurface } from "components/ShellCard";
import SectionCard from "components/SectionCard";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import api from "services/api";
import { useAuth } from "hooks/auth";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import { fmtBRL, fmtData, labelSituacao } from "views/Empresa/ConferenciaFatura/formatters";
import { rotuloDocumento } from "utils/modeloDocumento";
import { StatusBadge } from "views/Empresa/ConferenciaFatura/componentesCompartilhados";
import { useDetalheCte, ConciliacaoModal } from "views/Empresa/ConferenciaFatura/modais";

import {
  PERMISSAO_DECIDIR,
  temPermissao,
  pedidoDaUrl,
  urlAuditoriaFaturas,
  rotuloIndicio,
  textoInelegivel,
  alternarSelecao,
  selecionarTodasElegiveis,
  faturasSelecionadas,
  observacaoPadrao,
  veiculoMaisProximo,
  TOM_DIFERENCA,
} from "./regras";
import { baixarPdfAuditoria, arquivoPdfAuditoria } from "./relatorioPdf";

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  padding: "1rem 0.25rem",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.9rem",
};
const listaStyle = { margin: 0, paddingLeft: "1.1rem", display: "grid", gap: "0.45rem" };
const linkStyle = { background: "none", border: 0, padding: 0, color: AUTOPILOT_COLORS.accent, cursor: "pointer", fontWeight: 600 };
const TIMEOUT_RELATORIO_MS = 120000;

const mensagemErro = (e, padrao) =>
  (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
  (e && e.message) ||
  padrao;

function Aviso({ tone = "warning", children }) {
  return (
    <div
      role="note"
      style={{
        border: `1px solid var(--everlog-status-${tone})`,
        background: `color-mix(in srgb, var(--everlog-status-${tone}) 8%, transparent)`,
        borderRadius: 8,
        padding: "0.65rem 0.85rem",
        fontSize: "0.88rem",
        color: AUTOPILOT_COLORS.text,
        marginBottom: "1rem",
      }}
    >
      {children}
    </div>
  );
}

function BuscaPedido({ valorInicial, onBuscar, loading }) {
  const [valor, setValor] = useState(valorInicial || "");
  useEffect(() => setValor(valorInicial || ""), [valorInicial]);
  const enviar = (e) => {
    e.preventDefault();
    if (valor.trim()) onBuscar(valor.trim());
  };
  return (
    <ShellCard eyebrow="Pedido" title="Auditar um pedido" description="Informe o número do pedido que vem na nota fiscal (ex.: BR26134).">
      <form onSubmit={enviar} style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
        <Input
          id="auditoria-pedido-busca"
          aria-label="Número do pedido"
          placeholder="Número do pedido"
          value={valor}
          maxLength={20}
          onChange={(e) => {
            const v = e.target.value;
            setValor(v);
          }}
          style={{ maxWidth: 280 }}
        />
        <Button color="primary" className="btn-sm" type="submit" disabled={loading || !valor.trim()}>
          {loading ? <Spinner size="sm" /> : "Auditar"}
        </Button>
      </form>
    </ShellCard>
  );
}

function SecaoIndicios({ dossie }) {
  const { indicios = [], pendencias = [] } = dossie;
  if (!indicios.length && !pendencias.length) {
    return (
      <ShellCard eyebrow="Conferência" title="Indícios e pendências">
        <div style={stateBoxStyle}>Nenhum indício nem pendência neste pedido.</div>
      </ShellCard>
    );
  }
  return (
    <ShellCard eyebrow="Conferência" title="Indícios e pendências" description="Padrões encontrados nos números do pedido e o que falta ou não fecha.">
      {indicios.length > 0 && (
        <SectionCard variant="field">
          <ul style={listaStyle}>
            {indicios.map((i) => (
              <li key={i.codigo}>
                <StatusBadge tone={i.nivel === "alerta" ? "warning" : "info"}>{rotuloIndicio(i.codigo)}</StatusBadge>{" "}
                <span>{i.texto}</span>
                {i.codigo === "NENHUM_VEICULO_EXPLICA" && i.dados && (
                  <ul style={{ ...listaStyle, marginTop: "0.35rem" }}>
                    {i.dados.porCte.map((p) => {
                      const v = veiculoMaisProximo(p);
                      return (
                        <li key={p.cte} style={mutedStyle}>
                          {rotuloDocumento(p.modelo)} {p.cte}: cobrado {fmtBRL(p.cobrado)}
                          {v ? `; o mais próximo seria ${fmtBRL(v.esperado)} (${v.veiculo}, ${v.tabela})` : ""}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
      {pendencias.length > 0 && (
        <SectionCard variant="field" style={{ marginTop: "0.75rem" }}>
          <div style={{ ...mutedStyle, marginBottom: "0.4rem" }}>Pendências</div>
          <ul style={listaStyle}>
            {pendencias.map((p) => (
              <li key={p.codigo}>{p.texto}</li>
            ))}
          </ul>
        </SectionCard>
      )}
    </ShellCard>
  );
}

function SecaoCotacao({ cotacao }) {
  if (!cotacao) return null;
  return (
    <ShellCard eyebrow="Cotação" title={`Cotação ${cotacao.numero}`} description={`${cotacao.transportadora || "Transportadora não informada"} · versão ${cotacao.versao}`}>
      <SectionCard variant="field">
        <div style={{ display: "grid", gap: "0.35rem", fontSize: "0.88rem" }}>
          <div><span style={mutedStyle}>Frete cotado: </span>{fmtBRL(cotacao.valorFrete)}</div>
          {(cotacao.fretes || []).length > 1 && (
            <div>
              <span style={mutedStyle}>Fretes do pedido (somados no cotado): </span>
              {cotacao.fretes.map((f) => `${f.numero} · ${f.transportadora || "Transportadora não informada"} ${fmtBRL(f.valorFrete)}`).join(" · ")}
              {" = "}<strong>{fmtBRL(cotacao.valorFretePedido)}</strong>
            </div>
          )}
          {cotacao.veiculos.length > 0 && (
            <div><span style={mutedStyle}>Veículos: </span>{cotacao.veiculos.map((v) => `${v.quantidade} × ${v.tipo}`).join(", ")}</div>
          )}
          {cotacao.ofertas.length > 0 && (
            <div><span style={mutedStyle}>Ofertas: </span>{cotacao.ofertas.map((o) => `${o.transportadora} ${fmtBRL(o.valor)}`).join(" · ")}</div>
          )}
          {cotacao.servicos.length > 0 && (
            <div><span style={mutedStyle}>Serviços: </span>{cotacao.servicos.map((s) => `${s.tipo || "Serviço"} ${fmtBRL(s.valor)}`).join(" · ")}</div>
          )}
          {cotacao.observacoes && (
            <div>
              <div style={mutedStyle}>Observações (texto livre, não entra nos totais)</div>
              <div style={{ whiteSpace: "pre-wrap" }}>{cotacao.observacoes}</div>
            </div>
          )}
        </div>
      </SectionCard>
    </ShellCard>
  );
}

function SecaoRelatorio({ dossie, relatorio, estado, erro, onGerar }) {
  const desatualizado = relatorio && relatorio.hashDossie && relatorio.hashDossie !== dossie.hash;
  const r = relatorio && relatorio.relatorio;
  const acao = (
    <Button color="primary" className="btn-sm" onClick={onGerar} disabled={estado === "gerando" || !dossie.ctes.length}>
      {estado === "gerando" ? <Spinner size="sm" /> : r ? "Gerar de novo" : "Gerar relatório"}
    </Button>
  );
  return (
    <ShellCard
      eyebrow="Relatório"
      title="Relatório para o cliente"
      description="Texto redigido a partir dos números desta auditoria. Todo valor citado é conferido contra a auditoria antes de ser mostrado."
      action={acao}
    >
      {estado === "gerando" && (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
          <span>Gerando o relatório. Isso leva alguns segundos.</span>
        </div>
      )}
      {erro && <Aviso tone="danger">{erro}</Aviso>}
      {!dossie.ctes.length && <div style={stateBoxStyle}>O pedido ainda não tem CT-e: não há frete cobrado para relatar.</div>}
      {dossie.ctes.length > 0 && !r && estado !== "gerando" && !erro && (
        <div style={stateBoxStyle}>Nenhum relatório gerado para este pedido ainda.</div>
      )}
      {r && (
        <>
          {desatualizado && (
            <Aviso>Os dados do pedido mudaram desde que este relatório foi gerado. Gere de novo para atualizar.</Aviso>
          )}
          <SectionCard variant="field">
            <p style={{ marginBottom: "0.75rem" }}>{r.resumo}</p>
            {r.conferencia.length > 0 && (
              <>
                <div style={{ ...mutedStyle, marginBottom: "0.35rem" }}>O que a conferência mostrou</div>
                <ul style={{ ...listaStyle, marginBottom: "0.75rem" }}>
                  {r.conferencia.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </>
            )}
            {r.outrosPontos.length > 0 && (
              <>
                <div style={{ ...mutedStyle, marginBottom: "0.35rem" }}>Outros pontos do pedido</div>
                <ul style={{ ...listaStyle, marginBottom: "0.75rem" }}>
                  {r.outrosPontos.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </>
            )}
            <div style={{ ...mutedStyle, marginBottom: "0.35rem" }}>O que precisa de decisão</div>
            <ol style={listaStyle}>
              {r.decisoes.map((d, i) => (
                <li key={i}>
                  <strong>{d.pergunta}</strong>
                  <div style={mutedStyle}>Recomendação: {d.recomendacao}</div>
                </li>
              ))}
            </ol>
          </SectionCard>
          <div style={{ ...mutedStyle, marginTop: "0.5rem" }}>
            Gerado em {fmtData(relatorio.createdAt)}
            {relatorio.geradoPor && relatorio.geradoPor.nome ? ` por ${relatorio.geradoPor.nome}` : ""}.
          </div>
        </>
      )}
    </ShellCard>
  );
}

function SecaoDecisao({ dossie, podeDecidir, selecionadas, setSelecionadas, onAcao, resultadoLote }) {
  const { faturas = [] } = dossie;
  if (!faturas.length) return null;
  const elegiveis = faturas.filter((f) => f.elegivelLote);
  const qtd = selecionadas.size;
  const acoes = podeDecidir && elegiveis.length > 0 && (
    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
      <Button color="success" className="btn-sm" disabled={!qtd} onClick={() => onAcao("APROVAR")}>
        Aprovar com justificativa
      </Button>
      <Button color="danger" className="btn-sm" disabled={!qtd} onClick={() => onAcao("DEVOLVER")}>
        Devolver à transportadora
      </Button>
    </div>
  );
  return (
    <ShellCard
      eyebrow="Decisão"
      title="Decidir as faturas do pedido"
      description="Só entram no lote as faturas que têm apenas CT-es deste pedido e estão em análise, contestadas ou em tratativa."
      action={acoes}
    >
      {!podeDecidir && (
        <Aviso tone="info">Você pode consultar, mas não tem permissão para aprovar ou devolver faturas.</Aviso>
      )}
      <TableSurface>
        <Table size="sm" responsive className="hub-thead-accent">
          <thead className="text-primary">
            <tr>
              <th style={{ width: 36 }}>
                {podeDecidir && elegiveis.length > 0 && (
                  <Input
                    type="checkbox"
                    aria-label="Selecionar todas as elegíveis"
                    style={{ position: "static", margin: 0 }}
                    checked={qtd > 0 && qtd === elegiveis.length}
                    onChange={() =>
                      setSelecionadas(qtd === elegiveis.length ? new Set() : selecionarTodasElegiveis(faturas))
                    }
                  />
                )}
              </th>
              <th>Fatura</th>
              <th>Situação</th>
              <th>Vencimento</th>
              <th className="text-right">Valor</th>
              <th>Documentos do pedido</th>
              <th>Lote</th>
            </tr>
          </thead>
          <tbody>
            {faturas.map((f) => (
              <tr key={f.id}>
                <td>
                  {podeDecidir && f.elegivelLote && (
                    <Input
                      type="checkbox"
                      aria-label={`Selecionar fatura ${f.numero}`}
                      style={{ position: "static", margin: 0 }}
                      checked={selecionadas.has(f.id)}
                      onChange={() => setSelecionadas((atual) => alternarSelecao(atual, f))}
                    />
                  )}
                </td>
                <td style={{ fontWeight: 600 }}>{f.numero}</td>
                <td>
                  <StatusBadge codigo={f.situacao && f.situacao.codigo}>
                    {labelSituacao(f.situacao && f.situacao.codigo, f.situacao && f.situacao.nome)}
                  </StatusBadge>
                </td>
                <td>{fmtData(f.vencimento)}</td>
                <td className="text-right">{fmtBRL(f.valor)}</td>
                <td>{(f.ctes || []).join(", ")}</td>
                <td style={mutedStyle}>{f.elegivelLote ? "Elegível" : textoInelegivel(f)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableSurface>
      {resultadoLote && (
        <SectionCard variant="field" style={{ marginTop: "0.75rem" }}>
          <div style={{ fontWeight: 600, marginBottom: "0.35rem" }}>
            {resultadoLote.titulo}: {resultadoLote.ok} de {resultadoLote.total} com sucesso
          </div>
          <ul style={listaStyle}>
            {resultadoLote.itens.map((r) => (
              <li key={r.idFatura} style={{ color: r.ok ? undefined : "var(--everlog-status-danger)" }}>
                Fatura {r.numero}: {r.ok ? labelSituacao(r.situacaoNova, r.situacaoNova) || "ok" : r.erro}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}
    </ShellCard>
  );
}

export default function AuditoriaPedido() {
  const location = useLocation();
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { empresa, isAllEmpresas } = useCompanies();
  const embarcadorId = empresa && !isAllEmpresas ? empresa.value : null;
  const pedido = pedidoDaUrl(location.search);

  const [dossie, setDossie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [relatorio, setRelatorio] = useState(null);
  const [estadoRelatorio, setEstadoRelatorio] = useState("ocioso");
  const [erroRelatorio, setErroRelatorio] = useState(null);
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [acaoLote, setAcaoLote] = useState(null);
  const [anexosLote, setAnexosLote] = useState([]);
  const [resultadoLote, setResultadoLote] = useState(null);

  const detalheCte = useDetalheCte();
  const podeDecidir = temPermissao(user, PERMISSAO_DECIDIR);

  const carregar = useCallback(async () => {
    if (!pedido) {
      setDossie(null);
      return;
    }
    setLoading(true);
    setErro(null);
    try {
      const params = { pedido, ...(embarcadorId ? { embarcadorId } : {}) };
      const [resDossie, resRelatorio] = await Promise.all([
        api.get("/auditoria-pedido", { params }),
        api.get("/auditoria-pedido/relatorio", { params }).catch(() => ({ data: { relatorio: null } })),
      ]);
      setDossie(resDossie.data);
      setRelatorio((resRelatorio.data && resRelatorio.data.relatorio) || null);
      setSelecionadas(new Set());
    } catch (e) {
      setDossie(null);
      setErro(mensagemErro(e, "Não foi possível montar a auditoria do pedido."));
    } finally {
      setLoading(false);
    }
  }, [pedido, embarcadorId]);

  useEffect(() => {
    setResultadoLote(null);
    setErroRelatorio(null);
    setEstadoRelatorio("ocioso");
    carregar();
  }, [carregar]);

  const buscar = (valor) => {
    history.replace({ pathname: location.pathname, search: `?pedido=${encodeURIComponent(valor)}` });
  };

  const gerarRelatorio = async () => {
    setEstadoRelatorio("gerando");
    setErroRelatorio(null);
    try {
      const res = await api.post(
        "/auditoria-pedido/relatorio",
        { pedido, ...(embarcadorId ? { embarcadorId } : {}) },
        { timeout: TIMEOUT_RELATORIO_MS }
      );
      setRelatorio(res.data && res.data.relatorio);
      setEstadoRelatorio("pronto");
    } catch (e) {
      setErroRelatorio(mensagemErro(e, "Não foi possível gerar o relatório agora."));
      setEstadoRelatorio("erro");
    }
  };

  const baixarPdf = async () => {
    try {
      await baixarPdfAuditoria(dossie, relatorio && relatorio.relatorio);
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "PDF", description: mensagemErro(e, "Não foi possível gerar o PDF.") });
    }
  };

  const abrirAcao = async (acao) => {
    let anexos = [];
    if (acao === "DEVOLVER") {
      try {
        anexos = [await arquivoPdfAuditoria(dossie, relatorio && relatorio.relatorio)];
      } catch (e) {
        anexos = [];
      }
    }
    setAnexosLote(anexos);
    setAcaoLote(acao);
  };

  const faturasDoLote = useMemo(
    () => (dossie ? faturasSelecionadas(dossie.faturas, selecionadas) : []),
    [dossie, selecionadas]
  );
  const observacaoLote = useMemo(() => (acaoLote ? observacaoPadrao(acaoLote, pedido) : ""), [acaoLote, pedido]);

  const aoConcluirLote = (resultados, acao) => {
    const porId = new Map((dossie ? dossie.faturas : []).map((f) => [f.id, f.numero]));
    const itens = (resultados || []).map((r) => ({ ...r, numero: porId.get(r.idFatura) || r.idFatura }));
    setResultadoLote({
      titulo: acao === "APROVAR" ? "Aprovar com justificativa" : "Devolver à transportadora",
      ok: itens.filter((r) => r.ok).length,
      total: itens.length,
      itens,
    });
    carregar();
  };

  const t = dossie && dossie.totais;

  return (
    <div className="content">
      <PageHero
        eyebrow="Flow · Operação"
        title="Auditoria do Pedido"
        description="Cotado, conferido pela tabela e cobrado de um pedido, com os CT-es, as faturas e o que precisa de decisão."
        actions={
          <>
            {dossie && (
              <Button color="secondary" className="btn-sm" outline onClick={baixarPdf}>
                Baixar PDF
              </Button>
            )}
            <PageHeroRefreshButton onClick={carregar} loading={loading} disabled={!pedido} />
          </>
        }
      />

      <BuscaPedido valorInicial={pedido} onBuscar={buscar} loading={loading} />

      {!pedido && (
        <ShellCard>
          <div style={stateBoxStyle}>
            Informe um pedido acima, ou abra a partir do botão "Auditar frete" da cotação ou de uma fatura da Auditoria de Faturas.
          </div>
        </ShellCard>
      )}

      {pedido && loading && !dossie && (
        <ShellCard>
          <div style={stateBoxStyle}>
            <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
            <span>Montando a auditoria do pedido {pedido}…</span>
          </div>
        </ShellCard>
      )}

      {erro && (
        <ShellCard>
          <div style={stateBoxStyle} role="alert">{erro}</div>
        </ShellCard>
      )}

      {dossie && (
        <>
          <MetricCardGroup
            eyebrow={`Pedido ${dossie.pedido}${dossie.embarcador && dossie.embarcador.nome ? ` · ${dossie.embarcador.nome}` : ""}`}
            title="Cotado × conferido × cobrado"
            description={`${t.qtdCtes} documento(s), ${t.qtdNfs} nota(s) fiscal(is), ${t.qtdFaturas} fatura(s).`}
          >
            <MetricCard
              label="Cotado"
              value={t.cotado != null ? fmtBRL(t.cotado) : "Não vinculada"}
              helper={
                !dossie.cotacao
                  ? "Sem cotação com este pedido"
                  : (dossie.cotacao.fretes || []).length > 1
                  ? `Soma de ${dossie.cotacao.fretes.length} fretes do pedido`
                  : `Cotação ${dossie.cotacao.numero}`
              }
              iconClassName="tim-icons icon-paper"
              tone="info"
            />
            <MetricCard
              label="Conferido pela tabela"
              value={fmtBRL(t.conferido)}
              helper="Frete que a tabela da transportadora determina"
              iconClassName="tim-icons icon-check-2"
              tone="success"
            />
            <MetricCard
              label="Cobrado"
              value={fmtBRL(t.cobrado)}
              helper="Soma dos documentos"
              iconClassName="tim-icons icon-money-coins"
              tone="neutral"
            />
            <MetricCard
              label="Cobrado − conferido"
              value={fmtBRL(t.difCobradoConferido)}
              helper={t.difCobradoCotado != null ? `Cobrado − cotado: ${fmtBRL(t.difCobradoCotado)}` : undefined}
              iconClassName="tim-icons icon-alert-circle-exc"
              tone={TOM_DIFERENCA(t.difCobradoConferido)}
            />
          </MetricCardGroup>

          {dossie.vinculo && dossie.vinculo.cotacao === "nao_encontrada" && dossie.ctes.length > 0 && (
            <Aviso>
              Nenhuma cotação com este número de pedido. Informe o Nº do pedido na cotação (Listagem de Cotações › Editar cotação) para comparar com o cotado.
            </Aviso>
          )}

          <Row>
            <Col xl="12">
              <ShellCard eyebrow="Documentos" title="Documentos do pedido" description="Clique no documento para ver a composição cobrado × conferido.">
                {dossie.ctes.length === 0 ? (
                  <div style={stateBoxStyle}>Nenhum documento com este pedido.</div>
                ) : (
                  <TableSurface>
                    <Table size="sm" responsive hover className="hub-thead-accent">
                      <thead className="text-primary">
                        <tr>
                          <th>Documento</th>
                          <th>Emissão</th>
                          <th className="text-right">Peso (kg)</th>
                          <th className="text-right">Valor das NFs</th>
                          <th className="text-right">Cobrado</th>
                          <th className="text-right">Conferido</th>
                          <th className="text-right">Diferença</th>
                          <th>Fatura</th>
                          <th>Vencimento</th>
                          <th>Situação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dossie.ctes.map((c) => (
                          <tr key={c.id}>
                            <td>
                              <button type="button" style={linkStyle} onClick={() => detalheCte.abrir({ id: c.id, numero: c.numero })}>
                                {c.numero}
                              </button>
                              <div style={mutedStyle}>{rotuloDocumento(c.modelo)} · NF {c.nfs.join(", ")}</div>
                            </td>
                            <td>{fmtData(c.emissao)}</td>
                            <td className="text-right">{c.pesoKg ? c.pesoKg.toLocaleString("pt-BR") : "—"}</td>
                            <td className="text-right">{fmtBRL(c.valorCarga)}</td>
                            <td className="text-right">{fmtBRL(c.cobrado)}</td>
                            <td className="text-right">{c.conferido != null ? fmtBRL(c.conferido) : <span style={mutedStyle}>sem cálculo</span>}</td>
                            <td className="text-right" style={{ color: c.diferenca > 0.009 ? "var(--everlog-status-danger)" : undefined }}>
                              {c.diferenca != null ? fmtBRL(c.diferenca) : "—"}
                            </td>
                            <td>
                              {c.fatura ? (
                                <button type="button" style={linkStyle} onClick={() => history.push(urlAuditoriaFaturas(dossie.pedido))}>
                                  {c.fatura.numero}
                                </button>
                              ) : (
                                <span style={mutedStyle}>sem fatura</span>
                              )}
                            </td>
                            <td>{c.fatura ? fmtData(c.fatura.vencimento) : "—"}</td>
                            <td>
                              {c.fatura ? (
                                <StatusBadge codigo={c.fatura.situacao.codigo}>
                                  {labelSituacao(c.fatura.situacao.codigo, c.fatura.situacao.nome)}
                                </StatusBadge>
                              ) : "—"}
                            </td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700 }}>
                          <td>Total</td>
                          <td />
                          <td className="text-right">{t.pesoKg ? t.pesoKg.toLocaleString("pt-BR") : "—"}</td>
                          <td className="text-right">{fmtBRL(t.valorCarga)}</td>
                          <td className="text-right">{fmtBRL(t.cobrado)}</td>
                          <td className="text-right">{fmtBRL(t.conferido)}</td>
                          <td className="text-right">{fmtBRL(t.difCobradoConferido)}</td>
                          <td colSpan={3} />
                        </tr>
                      </tbody>
                    </Table>
                  </TableSurface>
                )}
              </ShellCard>
            </Col>
            <Col xl="7">
              <SecaoIndicios dossie={dossie} />
            </Col>
            <Col xl="5">
              <SecaoCotacao cotacao={dossie.cotacao} />
            </Col>
            <Col xl="12">
              <SecaoRelatorio
                dossie={dossie}
                relatorio={relatorio}
                estado={estadoRelatorio}
                erro={erroRelatorio}
                onGerar={gerarRelatorio}
              />
            </Col>
            <Col xl="12">
              <SecaoDecisao
                dossie={dossie}
                podeDecidir={podeDecidir}
                selecionadas={selecionadas}
                setSelecionadas={setSelecionadas}
                onAcao={abrirAcao}
                resultadoLote={resultadoLote}
              />
            </Col>
          </Row>
        </>
      )}

      {detalheCte.elemento}
      <ConciliacaoModal
        isOpen={Boolean(acaoLote)}
        onClose={() => setAcaoLote(null)}
        faturas={faturasDoLote}
        acao={acaoLote}
        observacaoInicial={observacaoLote}
        anexosIniciais={anexosLote}
        onDone={aoConcluirLote}
      />
    </div>
  );
}
